import { isEmpty, noop } from 'lodash';
import { List, Spacer, Text } from '@vinted/web-ui';
import { MEMBER_TEMPLATES_URL } from 'constants/routes';
import { VERIFICATION_LIST } from 'constants/index';
import useTranslate from 'hooks/useTranslate';
import { FormattedDateMessage } from 'components/@support';
import SeparatedList from 'components/SeparatedList';
import UserInfoBlock from './UserInfoBlock';
import FollowInfo from '../FollowInfo';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

const UserInfo = _ref => {
  let {
    user,
    showSupportFunctionality,
    canSendTemplates,
    viewingSelf,
    onTogglePoliciesModal = noop
  } = _ref;
  const translate = useTranslate('user');
  const {
    email,
    last_loged_on_ts,
    created_at,
    followers_count,
    following_count,
    msg_template_count,
    expose_location,
    city,
    country_title_local,
    id,
    business: isBusinessAccount
  } = user;
  const memberSinceDate = new Date(created_at);

  const renderLocationContent = () => {
    var _user$business_accoun, _user$business_accoun2;

    const hidePersonalInfo = isBusinessAccount && ((_user$business_accoun = user.business_account) === null || _user$business_accoun === void 0 ? void 0 : _user$business_accoun.entity_type) === 'sole_trader';
    const businessAddress = isBusinessAccount && ((_user$business_accoun2 = user.business_account) === null || _user$business_accoun2 === void 0 ? void 0 : _user$business_accoun2.address);
    const locationDescription = expose_location && city ? [city, country_title_local].join(', ') : country_title_local;

    if (locationDescription && !businessAddress || hidePersonalInfo) {
      return locationDescription;
    }

    if (businessAddress && !hidePersonalInfo) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [(businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.line1) && /*#__PURE__*/_jsx("div", {
          children: businessAddress.line1
        }), (businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.line2) && /*#__PURE__*/_jsx("div", {
          children: businessAddress.line2
        }), (businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.postal_code) && (businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.city) && /*#__PURE__*/_jsxs("div", {
          children: [businessAddress.postal_code, " ", businessAddress.city]
        }), (businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.country_title) && /*#__PURE__*/_jsx("div", {
          children: businessAddress.country_title
        })]
      });
    }

    return null;
  };

  const renderLocationBlock = () => {
    if (!renderLocationContent()) {
      return null;
    }

    return /*#__PURE__*/_jsx(List.Item, {
      children: /*#__PURE__*/_jsx(UserInfoBlock, {
        iconName: "location-pin",
        testId: "profile-location-info",
        children: renderLocationContent()
      })
    });
  };

  function renderAbout() {
    var _user$business_accoun3, _user$business_accoun4, _user$business_accoun5;

    const hidePersonalInfo = isBusinessAccount && ((_user$business_accoun3 = user.business_account) === null || _user$business_accoun3 === void 0 ? void 0 : _user$business_accoun3.entity_type) === 'sole_trader';
    const businessEmail = isBusinessAccount && ((_user$business_accoun4 = user.business_account) === null || _user$business_accoun4 === void 0 ? void 0 : _user$business_accoun4.email);
    const businessPhone = isBusinessAccount && ((_user$business_accoun5 = user.business_account) === null || _user$business_accoun5 === void 0 ? void 0 : _user$business_accoun5.phone_number);
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Text, {
        type: Text.Type.Caption,
        text: translate('about.title')
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Small
      }), /*#__PURE__*/_jsxs(List, {
        size: List.Size.Narrow,
        children: [renderLocationBlock(), businessEmail && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "envelope",
            testId: "profile-business-email",
            children: hidePersonalInfo ? /*#__PURE__*/_jsx("span", {
              children: businessEmail
            }) : /*#__PURE__*/_jsx("a", {
              href: "mailto:".concat(businessEmail),
              children: businessEmail
            })
          })
        }), businessPhone && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "phone",
            testId: "profile-business-phone",
            children: hidePersonalInfo ? /*#__PURE__*/_jsx("span", {
              children: businessPhone
            }) : /*#__PURE__*/_jsx("a", {
              href: "tel:".concat(businessPhone),
              children: businessPhone
            })
          })
        }), last_loged_on_ts && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "clock",
            children: /*#__PURE__*/_jsx(FormattedDateMessage, {
              id: "user.last_login",
              date: new Date(last_loged_on_ts),
              relativeDate: true
            })
          })
        }), /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "rss",
            children: /*#__PURE__*/_jsx(FollowInfo, {
              followersCount: followers_count,
              followingCount: following_count,
              userId: id,
              viewingSelf: viewingSelf
            })
          })
        }), showSupportFunctionality && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "member",
            children: /*#__PURE__*/_jsx(FormattedDateMessage, {
              id: "user.since",
              date: memberSinceDate
            })
          })
        }), showSupportFunctionality && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "envelope",
            children: email
          })
        }), canSendTemplates && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "flag",
            children: /*#__PURE__*/_jsx("a", {
              href: MEMBER_TEMPLATES_URL(user.id),
              children: translate('admin.template_count', {
                count: msg_template_count
              }, {
                count: msg_template_count
              })
            })
          })
        }), isBusinessAccount && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "document",
            children: /*#__PURE__*/_jsx("span", {
              onClick: onTogglePoliciesModal,
              "data-testid": "seller-policies-modal-toggle",
              children: /*#__PURE__*/_jsx(Text, {
                clickable: true,
                text: translate('seller_policies.title')
              })
            })
          })
        })]
      })]
    });
  }

  function renderVerifications() {
    var _user$business_accoun6, _user$business_accoun7, _user$business_accoun8;

    const filteredVerifications = VERIFICATION_LIST.filter(item => {
      var _user$verification$it;

      return (_user$verification$it = user.verification[item]) === null || _user$verification$it === void 0 ? void 0 : _user$verification$it.valid;
    });
    if (isEmpty(filteredVerifications) && !isBusinessAccount) return null;
    const businessLegalCode = isBusinessAccount && ((_user$business_accoun6 = user.business_account) === null || _user$business_accoun6 === void 0 ? void 0 : _user$business_accoun6.legal_code);
    const businessRegistrarName = isBusinessAccount && ((_user$business_accoun7 = user.business_account) === null || _user$business_accoun7 === void 0 ? void 0 : _user$business_accoun7.registrar_name);
    const selfCertified = isBusinessAccount && ((_user$business_accoun8 = user.business_account) === null || _user$business_accoun8 === void 0 ? void 0 : _user$business_accoun8.verified_identity);
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Text, {
        type: Text.Type.Caption,
        text: translate('verification.title')
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Small
      }), /*#__PURE__*/_jsxs(List, {
        size: List.Size.Narrow,
        children: [filteredVerifications.map(verification => /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "check-circle",
            children: translate("verification.".concat(verification))
          })
        }, verification)), isBusinessAccount && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "check-circle",
            testId: "business-verification-identity",
            children: translate('verification.business_identity')
          })
        }), businessLegalCode && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsxs(UserInfoBlock, {
            iconName: "check-circle",
            testId: "business-verification-legal-code",
            children: [translate('verification.legal_code'), " ", businessLegalCode]
          })
        }), businessRegistrarName && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "check-circle",
            testId: "business-verification-registrar-name",
            children: businessRegistrarName
          })
        }), selfCertified && /*#__PURE__*/_jsx(List.Item, {
          children: /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "check-circle",
            testId: "business-verification-self-certified",
            children: translate('verification.complies_with_eu_law')
          })
        })]
      })]
    });
  }

  return /*#__PURE__*/_jsx("div", {
    className: "u-flexbox",
    children: /*#__PURE__*/_jsxs(SeparatedList, {
      separator: /*#__PURE__*/_jsx(Spacer, {
        orientation: Spacer.Orientation.Vertical,
        size: Spacer.Size.X4Large
      }),
      children: [renderAbout(), renderVerifications()]
    })
  });
};

export default UserInfo;